import { Link, useLocation } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import PageHeader from '../../components/site/PageHeader';
import SEO from '../../components/SEO';
import contactInfo, { getHoursLine, getWhatsAppUrl } from '../../configs/contactInfo';
import { useSiteConfig } from '../../configs/siteConfig';
import { formatDate } from '../../utils/format';
import './legal.css';

// Shared frame for the Terms, Privacy and Refund pages.

export const POLICIES_UPDATED = '2026-10-04';

const POLICIES = [
  { to: '/terms', label: 'Terms of Service' },
  { to: '/privacy', label: 'Privacy Policy' },
  { to: '/refund', label: 'Refund Policy' },
];

/** "Questions?" box at the end of each policy. No email until a mailbox exists. */
export const LegalContact = () => {
  useSiteConfig();
  return (
    <div className="lg-contact">
      <dl className="pk-rows">
        <div>
          <dt>Business</dt>
          <dd>{contactInfo.companyName}</dd>
        </div>
        <div>
          <dt>Owner</dt>
          <dd>{contactInfo.owner.name}</dd>
        </div>
        <div>
          <dt>Phone and WhatsApp</dt>
          <dd>
            <a href={`tel:${contactInfo.phone.primary.replace(/\s/g, '')}`}>
              {contactInfo.phone.display}
            </a>
          </dd>
        </div>
        {contactInfo.email.support && (
          <div>
            <dt>Email</dt>
            <dd>
              <a href={`mailto:${contactInfo.email.support}`}>
                {contactInfo.email.support}
              </a>
            </dd>
          </div>
        )}
        <div>
          <dt>Support hours</dt>
          <dd>{getHoursLine()}</dd>
        </div>
        <div>
          <dt>Location</dt>
          <dd>
            {contactInfo.address.city}, {contactInfo.address.state},{' '}
            {contactInfo.address.country}
          </dd>
        </div>
      </dl>
      <div className="pk-cluster lg-contact__actions">
        <Link className="pk-btn pk-btn--primary pk-btn--sm" to="/contact">
          Contact us
        </Link>
        <a
          className="pk-btn pk-btn--secondary pk-btn--sm"
          href={getWhatsAppUrl(`Hi ${contactInfo.companyName}, I have a question.`)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <MessageCircle aria-hidden="true" />
          WhatsApp
        </a>
      </div>
    </div>
  );
};

const LegalLayout = ({ title, subtitle, seo, children }) => {
  const { pathname } = useLocation();

  return (
    <main className="pk-page">
      <SEO {...seo} />
      <div className="pk-container pk-container--narrow">
        <PageHeader
          title={title}
          subtitle={subtitle}
          crumbs={[{ label: 'Home', to: '/' }, { label: title }]}
        />
        <nav className="lg-nav" aria-label="Policies">
          {POLICIES.map(policy => {
            const current = pathname === policy.to;
            return (
              <Link
                key={policy.to}
                to={policy.to}
                className={`pk-chip${current ? ' is-active' : ''}`}
                aria-current={current ? 'page' : undefined}
              >
                {policy.label}
              </Link>
            );
          })}
        </nav>
        <p className="lg-updated">
          Last updated: <time dateTime={POLICIES_UPDATED}>{formatDate(POLICIES_UPDATED)}</time>
        </p>
        <article className="pk-prose lg-prose">{children}</article>
      </div>
    </main>
  );
};

export default LegalLayout;
